// backendAPI.js frontend - FIXED: Better error handling and fallback strategy

const FIREBASE_FUNCTIONS_URL = 'https://us-central1-fir-7c9c9.cloudfunctions.net';
/**
 * ✅ FRONTEND MEMORY CACHE
 * Stores news in memory for 6 hours
 * Prevents frontend from calling backend if data is fresh
 * Saves bandwidth AND backend costs
 */

let frontendNewsCache = {};
let cacheTimes = {};
const CACHE_DURATION_MS = 6 * 60 * 60 * 1000; // 6 hours

function isCacheFresh(sourceId) {
  if (!cacheTimes[sourceId]) return false;
  
  const age = Date.now() - cacheTimes[sourceId];
  return age < CACHE_DURATION_MS;
}

function getCacheAge(sourceId) {
  if (!cacheTimes[sourceId]) return null;
  const age = Date.now() - cacheTimes[sourceId];
  const minutes = Math.floor(age / 60000);
  const hours = Math.floor(age / 3600000);
  
  if (minutes < 1) return `${Math.floor(age / 1000)}s ago`;
  if (minutes < 60) return `${minutes}m ago`;
  return `${hours}h ago`;
}

function getRemainingCacheTime(sourceId) {
  if (!cacheTimes[sourceId]) return null;
  const age = Date.now() - cacheTimes[sourceId];
  const remaining = CACHE_DURATION_MS - age;
  const minutes = Math.floor(remaining / 60000);
  const hours = Math.floor(remaining / 3600000);
  
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
}

function setCacheData(sourceId, data) {
  frontendNewsCache[sourceId] = data;
  cacheTimes[sourceId] = Date.now();
  console.log(`💾 FRONTEND CACHE SET: ${sourceId} (expires in 6h)`);
}

function getCacheData(sourceId) {
  return frontendNewsCache[sourceId] || null;
}
// ============================================
// HTML ENTITY DECODING
// ============================================
const decodeHtmlEntities = (text) => {
  if (!text) return '';
  return text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&#x27;/g, "'")
    .replace(/&#x2F;/g, '/')
    .replace(/&#160;/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(dec))
    .replace(/&#x([0-9a-f]+);/gi, (match, hex) => String.fromCharCode(parseInt(hex, 16)))
    .trim();
};

// ============================================
// SOURCE KEY MAPPING - Handle custom source IDs
// ============================================
const SOURCE_KEY_MAP = {
  'variety': 'variety',
  'yahoo-sports': 'yahoo-sports',
  'cnbc': 'cnbc',
  'cnn': 'cnn',
  'bbc': 'bbc',
  'abc': 'abc',
  'nbc': 'nbc',
  'fox': 'fox',
  'espn': 'espn',
  'reuters': 'reuters',
  'ap': 'ap',
  'guardian': 'guardian',
  'techcrunch': 'techcrunch'
};
export const refreshNewsViaBackendSimple = async (selectedSources, sourceConfig) => {
  try {
    const newsData = {};
    const loadedCards = [];
    const failedSources = [];
    const cachedSources = [];

    console.log(`\n╔═════════════════════════════════════════╗`);
    console.log(`║ 📰 NEWS REFRESH (6-Hour Cache)          ║`);
    console.log(`╚═════════════════════════════════════════╝`);
    console.log(`🔄 Checking ${selectedSources.length} sources...\n`);

    for (const sourceId of selectedSources) {
      try {
        const sourceName = sourceConfig[sourceId]?.name || sourceId;
        const sourceKey = sourceId;
        
        // ✅ CHECK CACHE FIRST
        const cacheAge = getCacheAge(sourceId);
        const remaining = getRemainingCacheTime(sourceId);
        
        if (isCacheFresh(sourceId)) {
          const cachedData = getCacheData(sourceId);
          
          if (cachedData) {
            console.log(`  ✅ ${sourceName}: CACHE HIT (${cacheAge}, expires in ${remaining})`);
            console.log(`     📊 Using ${cachedData.stories?.length || 0} cached articles`);
            
            // Use cached data without calling backend
            newsData[sourceId] = cachedData;
            cachedSources.push(sourceId);
            continue;
          }
        } else if (cacheAge) {
          console.log(`  ❌ ${sourceName}: Cache expired (${cacheAge}) - fetching fresh`);
        } else {
          console.log(`  🔍 ${sourceName}: Never cached - fetching fresh`);
        }

        // ✅ CACHE MISS - CALL BACKEND
        console.log(`     🌐 Calling backend...`);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);
        
        const response = await fetch(
          `https://us-central1-fir-7c9c9.cloudfunctions.net/performWebSearch?source=${encodeURIComponent(sourceKey)}`,
          {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Cache-Control': 'no-cache'
            },
            signal: controller.signal
          }
        );
        
        clearTimeout(timeoutId);

        if (!response.ok) {
          console.error(`     ❌ HTTP ${response.status}`);
          failedSources.push(sourceId);
          continue;
        }

        const data = await response.json();

        if (data.success && data.results && data.results.length > 0) {
          const articles = data.results.slice(0, 6);
          
          const sourceData = {
            name: sourceName,
            logo: sourceConfig[sourceId]?.logo || '',
            color: sourceConfig[sourceId]?.color || '#000000',
            stories: articles.map(article => ({
              title: article.title,
              time: getRelativeTimeString(article.datePublished),
              image: article.image,
              description: article.snippet,
              source: article.url
            }))
          };

          // ✅ SAVE TO FRONTEND CACHE
          setCacheData(sourceId, sourceData);

          // Show if it came from backend cache or fresh fetch
          const fromBackendCache = data.fromCache ? ' (via backend cache)' : ' (fresh fetch)';
          console.log(`     ✅ ${articles.length} articles${fromBackendCache}`);
          
          newsData[sourceId] = sourceData;
          loadedCards.push(sourceId);
          
        } else {
          console.log(`     ⚠️ No articles found`);
          failedSources.push(sourceId);
        }
        
      } catch (error) {
        console.error(`  ❌ ${sourceId} error:`, error.message);
        failedSources.push(sourceId);
      }
    }
    
    console.log(`\n╔═════════════════════════════════════════╗`);
    console.log(`║ 📊 RESULTS                              ║`);
    console.log(`╚═════════════════════════════════════════╝`);
    console.log(`✅ Loaded fresh: ${loadedCards.length}`);
    console.log(`💾 From cache: ${cachedSources.length}`);
    console.log(`❌ Failed: ${failedSources.length}`);
    console.log(`📊 Total sources: ${Object.keys(newsData).length}/${selectedSources.length}\n`);
    
    return {
      success: loadedCards.length > 0 || cachedSources.length > 0,
      newsData,
      loadedCards,
      cachedSources,
      failedSources,
      timestamp: new Date(),
      cacheStats: {
        fresh: loadedCards.length,
        cached: cachedSources.length,
        failed: failedSources.length,
        total: Object.keys(newsData).length
      }
    };
    
  } catch (error) {
    console.error('❌ Refresh failed:', error);
    return {
      success: false,
      error: error.message,
      newsData: {},
      loadedCards: [],
      cachedSources: [],
      failedSources: selectedSources
    };
  }
};


// ============================================
// WEB SEARCH FUNCTION
// ============================================
export const performWebSearchBackend = async (query, count = 10) => {
  try {
    console.log(`🔍 Backend Web Search: "${query}"`);
    
    const BACKEND_URL = `${FIREBASE_FUNCTIONS_URL}/performWebSearch`;
    
    const response = await fetch(`${BACKEND_URL}?q=${encodeURIComponent(query)}&count=${count}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache'
      }
    });

    const responseText = await response.text();
    console.log(`📡 Response status: ${response.status}`);
    
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      console.error(`❌ Failed to parse response:`, responseText.substring(0, 100));
      return {
        success: false,
        error: 'Failed to parse search results'
      };
    }

    if (!response.ok || !data.success) {
      console.error(`❌ Search failed:`, data.error);
      return {
        success: false,
        error: data.error || 'Search failed',
        availableSources: data.availableSources
      };
    }

    if (!data.results || data.results.length === 0) {
      console.warn(`⚠️ No results found for "${query}"`);
      return {
        success: false,
        error: 'No articles found'
      };
    }

    console.log(`✅ Found ${data.results.length} articles`);
    
    return {
      success: true,
      query: data.query,
      totalResults: data.totalResults,
      results: data.results,
      searchDate: data.searchDate
    };

  } catch (error) {
    console.error(`❌ Backend search error:`, error.message);
    return {
      success: false,
      error: error.message
    };
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

function getRelativeTimeString(dateString) {
  try {
    const articleDate = new Date(dateString);
    const now = new Date();
    const diffMs = now - articleDate;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return 'Today';
  } catch (e) {
    return 'Recently';
  }
}

// ============================================
// SOURCE CONFIGURATION
// ============================================

export function getDefaultSourceConfig() {
  return {
    cnn: { 
      name: 'CNN',
      color: '#cc0000', 
      logo: 'https://www.cnn.com/favicon.ico'
    },
    bbc: { 
      name: 'BBC',
      color: '#000000', 
      logo: 'https://images.icon-icons.com/70/PNG/512/bbc_news_14062.png'
    },
    abc: { 
      name: 'ABC',
      color: '#0000ff', 
      logo: 'https://abc.com/favicon.ico'
    },
    nbc: { 
      name: 'NBC',
      color: '#800080', 
      logo: 'https://www.nbc.com/favicon.ico'
    },
    fox: { 
      name: 'FOX',
      color: '#003366', 
      logo: 'https://www.foxnews.com/favicon.ico'
    },
    espn: { 
      name: 'ESPN',
      color: '#ff0000', 
      logo: 'https://www.espn.com/favicon.ico'
    },
    techcrunch: { 
      name: 'TechCrunch',
      color: '#0a9e32', 
      logo: 'https://techcrunch.com/wp-content/uploads/2015/02/cropped-cropped-favicon-gradient.png'
    },
    'yahoo-sports': {
      name: 'Yahoo Sports',
      color: '#6F00FF',
      logo: 'https://www.yahoo.com/favicon.ico'
    },
    variety: {
      name: 'Variety',
      color: '#000000',
      logo: 'https://www.nasdaq.com/sites/acquia.prod/files/2020/09/18/variety.jpg'
    },
    cnbc: {
      name: 'CNBC',
      color: '#CC0000',
      logo: 'https://www.cnbc.com/favicon.ico'
    },
    reuters: {
      name: 'Reuters',
      color: '#FF6600',
      logo: 'https://www.reuters.com/favicon.ico'
    },
    ap: {
      name: 'AP News',
      color: '#000000',
      logo: 'https://apnews.com/favicon.ico'
    },
    guardian: {
      name: 'The Guardian',
      color: '#0084C6',
      logo: 'https://www.theguardian.com/favicon.ico'
    }
  };
}

// ============================================
// YOUTUBE FUNCTIONS
// ============================================

export const fetchYouTubeTranscriptBackend = async (videoId) => {
  try {
    console.log(`📝 Fetching transcript for video: ${videoId}`);
    
    const response = await fetch(
      `${FIREBASE_FUNCTIONS_URL}/getYouTubeTranscript?videoId=${videoId}`
    );
    
    if (response.status === 404) {
      const data = await response.json();
      console.log(`ℹ️ No captions available for ${videoId}`);
      return null;
    }
    
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      console.error(`❌ Transcript fetch failed (${response.status}):`, data.error);
      return null;
    }
    
    const data = await response.json();
    
    if (data.success && data.transcript) {
      console.log(`✅ Transcript fetched: ${data.transcriptLength} characters`);
      return data.transcript;
    }
    
    console.warn('⚠️ Unexpected response format');
    return null;
    
  } catch (error) {
    console.error('❌ Transcript fetch failed:', error);
    return null;
  }
};

export const extractYouTubeId = (url) => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/shorts\/([^&\n?#]+)/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) return match[1];
  }
  return null;
};

export const isYouTubeURL = (url) => {
  if (!url) return false;
  return /youtube\.com\/watch|youtu\.be\/|youtube\.com\/embed|youtube\.com\/shorts/i.test(url);
};

export const fetchWebContentBackend = async (url) => {
  try {
    console.log('🌐 Fetching web content from backend:', url);
    
    const response = await fetch(
      `${FIREBASE_FUNCTIONS_URL}/fetchWebContent?url=${encodeURIComponent(url)}`
    );
    
    if (!response.ok) {
      const error = await response.json();
      console.error('❌ Backend error:', error);
      return {
        success: false,
        error: error.error || 'Failed to fetch content'
      };
    }
    
    const data = await response.json();
    
    if (data.success) {
      console.log(`✅ Web content received: ${data.content.length} chars`);
      return data;
    }
    
    return {
      success: false,
      error: 'No content received'
    };
  } catch (error) {
    console.error('❌ Backend fetch failed:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// ============================================
// EXPORTS
// ============================================

export default {
  fetchYouTubeTranscriptBackend,
  extractYouTubeId,
  isYouTubeURL,
  performWebSearchBackend,
  getDefaultSourceConfig,
  fetchWebContentBackend,
  decodeHtmlEntities,
  FIREBASE_FUNCTIONS_URL,
  refreshNewsViaBackendSimple
};